import { Divider, Flex, Layout, Menu, Radio, Typography } from "antd";
import Sider from "antd/lib/layout/Sider";
import cx from "clsx";
import Logo from "components/common/Logo/Logo";
import { UserMenu } from "components/custom";
import { configs } from "configs";
import { getAppCredentials } from "configs/utils";
import Notifications from "features/shared/components/notifications/notifications";
import { getAppLanguage } from "helpers/getAppLanguage";
import { useEffect, useMemo, useState } from "react";
import { useTranslation, withTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { setSelectedMenuItem, setSelectedSideMenuItem } from "store/appSlice";
import { Icon, Link, Space } from "../components/common";
import { getAppByKey, getCurrentApp } from "../core/appRegistry";
import styles from "./index.module.less";

const { Header, Content } = Layout;

// Get app menu items using app registry
const getAppMenuItems = async (appKey) => {
  try {
    const appConfig = getAppByKey(appKey);
    if (appConfig && appConfig.menuItems) {
      const menuModule = await appConfig.menuItems();
      return menuModule.default;
    }
  } catch (error) {
    console.error("Failed to load app menu items:", error);
  }
  return null;
};
const { Title } = Typography;

const AppLayout = ({ children, i18n }) => {
  const userPrivileges = useSelector((state) => state.app.privileges);
  const privileges = userPrivileges || getAppCredentials()?.privileges;
  const currentAppKey =
    useSelector((state) => state.app.currentApp) ||
    getCurrentApp()?.key ||
    "fms";
  const dispatch = useDispatch();
  const location = useLocation();
  const { t } = useTranslation();
  const currentLanguage = getAppLanguage();
  const [locale, setLocale] = useState(currentLanguage?.key || "en");
  const [loading, setLoading] = useState(false);
  const user = useSelector((state) => state.app.user?.info?.user);

  const [appMenuItems, setAppMenuItems] = useState(null);

  // Load app-specific menu items
  useEffect(() => {
    const loadMenuItems = async () => {
      const menuItems = await getAppMenuItems(currentAppKey);
      setAppMenuItems(menuItems);
    };
    loadMenuItems();
  }, [currentAppKey]);t

  const menuList = useMemo(() => {
    if (!appMenuItems || !privileges) return [];
    try {
      return appMenuItems.menuItems(privileges, false, t, currentAppKey);
    } catch (error) {
      console.error("Error generating menu items:", error);
      return [];
    }
  }, [appMenuItems, privileges, currentAppKey, t]);
  const selectedMenuItem = useSelector((state) => state.app.selectedMenuItem);
  const selectedSideMenuItem = useSelector(
    (state) => state.app.selectedSideMenuItem,
  );

  const sidBarItems = useMemo(() => {
    if (!appMenuItems || !privileges || !selectedMenuItem) {
      return [];
    }

    try {
      if (selectedMenuItem.hasSidebar === false) {
        return [];
      }
      const sidebarMenuKey = selectedMenuItem.key;
      const sidebarFunction = appMenuItems.sidebarMenuItems[sidebarMenuKey];
      if (!sidebarFunction) {
        return [];
      }
      return sidebarFunction(privileges, false, t, currentAppKey);
    } catch (error) {
      console.error("Error generating sidebar items:", error);
      return [];
    }
  }, [appMenuItems, privileges, selectedMenuItem, currentAppKey, t]);

  const checkIfPathMatches = (modulePath) => {
    const browserPath = location.pathname;
    const pathWithoutLang = browserPath.replace(/^\/(en|ar)/, "");
    const pathWithoutApp = pathWithoutLang.replace(/^\/(fms|rms)/, "");
    return !!pathWithoutApp.startsWith(modulePath?.split("?")[0]);
  };

  useEffect(() => {
    if (menuList && menuList.length > 0) {
      dispatch(
        setSelectedMenuItem(
          menuList.filter((e) => {
            return Array.isArray(e.path)
              ? e.path.some((e) => checkIfPathMatches(e))
              : checkIfPathMatches(e.path);
          })?.[0],
        ),
      );
    }
  }, [menuList, userPrivileges, location.pathname, dispatch]);

  useEffect(() => {
    if (sidBarItems && sidBarItems.length > 0) {
      dispatch(
        setSelectedSideMenuItem(
          sidBarItems.filter((e) => checkIfPathMatches(e.path))?.[0],
        ),
      );
    } else {
      dispatch(setSelectedSideMenuItem());
    }
  }, [sidBarItems, location.pathname, dispatch]);

  const onClickMenuItem = (e) => {
    if (e.domEvent.ctrlKey || e.domEvent.metaKey || e.domEvent.button === 2) {
      return;
    }
    const item = menuList.find((it) => it.key === e.key);
    if (!selectedMenuItem || item?.key !== selectedMenuItem?.key) {
      dispatch(setSelectedMenuItem(item));
      dispatch(setSelectedSideMenuItem());
    }
  };

  const onChangeLanguage = (e) => {
    const newLang = e.target.value;
    if (newLang === locale) return;
    setLoading(true);
    const lang = configs.languages.find((l) => l.key === newLang);
    if (lang) {
      const currentPath = location.pathname;
      const pathWithoutLang = currentPath.replace(/^\/(en|ar)/, "") || "/";
      const newUrl =
        window.location.origin +
        "/" +
        lang.key +
        pathWithoutLang +
        location.search;
      window.location.href = newUrl;
    } else {
      setLoading(false);
    }
  };

  useEffect(() => {
    const lang = getAppLanguage();
    if (lang?.key !== locale) {
      setLocale(lang?.key || "en");
    }
  }, [location.pathname]);

  return (
    <Layout hasSider={!!sidBarItems?.length}>
      <Layout>
        <Header className={styles.header}>
          <Flex align="center">
            <Link
              to={currentAppKey === "fms" ? "/dashboard" : "/bookings"}
              style={{ display: "flex", alignItems: "center" }}
            >
              <Logo width="137" />
            </Link>
            <Divider type="vertical" style={{ height: 24, marginInline: 16 }} />
            <Title className="m-0" level={3}>
              {currentAppKey === "rms"
                ? t("Booking Management")
                : t("Facility Management")}
            </Title>
          </Flex>
          <Flex style={{ flex: 1 }} gap={48}>
            <Menu
              style={{ justifyContent: "end" }}
              mode="horizontal"
              onClick={onClickMenuItem}
              selectedKeys={[selectedMenuItem?.key]}
              items={menuList}
            />
            <Space className={styles.navbarIcons} size={20}>
              {/* <Card className={cx(styles.bestTimeCard)}> */}
              {/* {user?.apps?.find((app) => app?.slug === "rms") && (
                <a href={"/admin/dashboard"} rel="noopener noreferrer">
                  <Text strong>{t("Switch to Legacy")}</Text>
                </a>
              )} */}
              {/* </Card> */}
              {configs.languages.length > 1 && (
                <Radio.Group
                  className={styles.languageSwitcher}
                  buttonStyle="solid"
                  value={locale}
                  size="small"
                  onChange={onChangeLanguage}
                  disabled={loading}
                >
                  {configs.languages?.map((e) => (
                    <Radio.Button key={e.key} value={e.key}>
                      <span>
                        <Icon icon="MdGTranslate" size={14} />
                      </span>
                      {e.label}
                    </Radio.Button>
                  ))}
                </Radio.Group>
              )}
              {(currentAppKey === "rms" || currentAppKey === "developer") && (
                <Notifications />
              )}
              <UserMenu />
            </Space>
          </Flex>
        </Header>
        <div
          className={cx(styles.sider, !!sidBarItems?.length && styles.hasSider)}
        >
          {!!sidBarItems?.length && (
            <Sider width={100} theme="light">
              <Menu
                selectedKeys={[selectedSideMenuItem?.key]}
                items={sidBarItems}
                onClick={(e) => {
                  const item = sidBarItems.find((it) => it.key === e.key);
                  dispatch(setSelectedSideMenuItem(item));
                }}
                className={styles.sideMenu}
              />
            </Sider>
          )}
          <Content className={styles.content}>{children}</Content>
        </div>
      </Layout>
    </Layout>
  );
};

export default withTranslation()(AppLayout);
